import React, { useMemo } from 'react';
import { CareerFocusWithMetrics } from '../../hooks/useCareerFocus';
import { useDarkMode } from '../../context/DarkModeContext';
import { motion } from 'framer-motion';
import { TabContainerProps } from '../../types/dashboardProps';
import { calculateReviewPrepActivation } from '../../utils/reviewPrepCalculations';

// Import tab components directly to avoid lazy loading issues with transitions
import MyGoalsTab from './MyGoalsTab';
import CompanyRubricTab from './CompanyRubricTab';
import EvidenceLibraryTab from './EvidenceLibraryTab';
import ResultsTab from './ResultsTab';
import ReviewPrepTab from './ReviewPrep/ReviewPrepTab';

const TabContainer: React.FC<TabContainerProps> = ({ 
  activeTab, 
  setActiveTab, 
  focus,
  // Goals data
  goals,
  goalsLoading,
  goalsError,
  refreshGoals,
  // Rubric data
  activeRubric,
  rubricLoading,
  rubricError,
  refreshRubric,
  // Cards data
  cards,
  cardsLoading,
  cardsError,
  refreshCards,
  // Additional props
  onQuickAdd
}) => {
  const { darkMode } = useDarkMode();
  
  const onTabChange = (tabId: 'goals' | 'rubric' | 'evidence' | 'results' | 'reviewprep') => {
    setActiveTab(tabId);
  };
  
  // Check if Review Prep tab should be visible
  const showReviewPrep = useMemo(() => {
    if (!focus?.start_date || !focus?.end_date) return false;
    const { isActive } = calculateReviewPrepActivation(focus.start_date, focus.end_date);
    return isActive;
  }, [focus?.start_date, focus?.end_date]);

  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const activeTabBg = darkMode ? 'bg-gray-700' : 'bg-gray-100';
  const activeTabText = darkMode ? 'text-blue-400' : 'text-blue-600';
  const inactiveTabText = darkMode ? 'text-gray-400' : 'text-gray-600';
  const hoverBg = darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50';

  const tabs = useMemo(() => {
    const baseTabs = [
      { id: 'goals' as const, label: 'My Goals', emoji: '🎯' },
      { id: 'rubric' as const, label: 'Company Rubric', emoji: '📋' },
      { id: 'evidence' as const, label: 'Evidence Library', emoji: '📚' }
    ];
    
    // Add Review Prep tab if active (second to last)
    if (showReviewPrep) {
      baseTabs.push({ id: 'reviewprep' as const, label: 'Review Prep', emoji: '✅' });
    }
    
    // Results tab is always last
    baseTabs.push({ id: 'results' as const, label: 'Results', emoji: '🏆' });
    
    return baseTabs;
  }, [showReviewPrep]);

  return (
    <div className={`tabs-container ${bgColor} rounded-lg shadow-sm border ${borderColor}`}>
      {/* Tab Headers */}
      <div className={`border-b ${borderColor} overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent`}>
        <div className="flex min-w-max">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={`px-4 sm:px-6 py-3 text-sm font-medium transition-colors relative whitespace-nowrap ${
                activeTab === tab.id 
                  ? `${activeTabText} ${activeTabBg}` 
                  : `${inactiveTabText} ${hoverBg}`
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <span className="text-lg">{tab.emoji}</span>
                {tab.label}
              </span>
              {activeTab === tab.id && (
                <motion.div
                  layoutId="activeTabIndicator"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500"
                  initial={false}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6 min-h-[600px]">
        <div className="relative">
          <motion.div
            key={activeTab}
            initial={false}
            animate={{ opacity: 1 }}
            transition={{ duration: 0 }}
          >
            {activeTab === 'goals' && (
              <MyGoalsTab 
                focus={focus} 
                goals={goals}
                goalsLoading={goalsLoading}
                goalsError={goalsError}
                refreshGoals={refreshGoals}
                activeRubric={activeRubric}
                onQuickAdd={onQuickAdd}
              />
            )}
            {activeTab === 'rubric' && (
              <CompanyRubricTab 
                focus={focus} 
                activeRubric={activeRubric}
                rubricLoading={rubricLoading}
                rubricError={rubricError}
                refreshRubric={refreshRubric}
                onQuickAdd={onQuickAdd}
              />
            )}
            {activeTab === 'evidence' && (
              <EvidenceLibraryTab 
                focus={focus} 
                cards={cards}
                cardsLoading={cardsLoading}
                cardsError={cardsError}
                refreshCards={refreshCards}
                activeRubric={activeRubric}
                onQuickAdd={onQuickAdd}
              />
            )}
            {activeTab === 'results' && (
              <ResultsTab focus={focus} />
            )}
            {activeTab === 'reviewprep' && (
              <ReviewPrepTab focus={focus} onQuickAdd={onQuickAdd} />
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TabContainer;